import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Sparkles } from 'lucide-react'
import type { TestRun } from '@sophia/shared-schemas'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { FindingCard } from './FindingCard'
import { deriveFindings } from '../lib/run-diagnostics'

interface Props {
  run: TestRun
}

function formatCost(usd: string | number | null): string {
  if (usd == null) return '—'
  const n = typeof usd === 'string' ? Number.parseFloat(usd) : usd
  if (Number.isNaN(n)) return '—'
  return `$${n.toFixed(4)}`
}

export function InsightsTab({ run }: Props) {
  const findings = run.metrics ? deriveFindings(run.metrics, 8) : []

  if (!run.llmInsights && findings.length === 0) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-fg-3">
            Sem insights pra esta run. Rode a base pra gerar o diagnóstico das métricas e a análise do LLM.
          </p>
        </CardBody>
      </Card>
    )
  }
  return (
    <div className="flex flex-col gap-5">
      {findings.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-display font-bold text-base text-fg-1 flex items-center gap-2">
              Recomendações
              <span className="data-mono text-sm text-fg-3 font-normal">({findings.length})</span>
            </h3>
            <p className="text-sm text-fg-3 mt-0.5">
              Achados estruturados a partir das métricas desta run, ordenados por impacto.
            </p>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {findings.map((f) => (
                <FindingCard key={f.id} finding={f} />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {run.llmInsights && <LlmAnalysisCard run={run} />}
    </div>
  )
}

function LlmAnalysisCard({ run }: Props) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display font-bold text-base text-fg-1 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-accent-600" />
              Insights do LLM
            </h3>
            <p className="text-sm text-fg-3 mt-0.5">
              Análise textual baseada na configuração das classes + métricas desta run. Não classifica nada, só recomenda.
            </p>
          </div>
          <div className="flex items-center gap-4 text-2xs text-fg-3">
            <span>
              Modelo: <span className="data-mono text-fg-2">{run.llmModel ?? '—'}</span>
            </span>
            <span>
              Tokens:{' '}
              <span className="data-mono text-fg-2">
                {run.llmTokensInput ?? 0}/{run.llmTokensOutput ?? 0}
              </span>
            </span>
            <span>
              Custo: <span className="data-mono text-fg-2">{formatCost(run.llmCostUsd)}</span>
            </span>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <div className="prose-sophia max-w-none text-sm text-fg-2">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{run.llmInsights ?? ''}</ReactMarkdown>
        </div>
      </CardBody>
    </Card>
  )
}
